import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, Router, CanActivateChild } from '@angular/router';
import { Store } from '@ngrx/store';
import { PermissionService } from '../services/permission.service';
import { FeatureSessionActions, FeatureSessionSelectors } from '@shared/store/session/index';
import { catchError, of, switchMap, tap, Observable } from 'rxjs';
import { AuthService } from '../services/auth.services';
import { getCurrentQueryParams } from '@core/utils/functions';
import { UserRoleType } from '@core/enums/enums';
import { SignInAsRoute } from '@core/constants';

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate, CanActivateChild {
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkPermission(route, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkPermission(route, state);
  }

  private checkPermission(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Check authentication first
    if (!this.authService.isAuthenticated()) {
      this.handleUnauthenticated(state.url);
      return of(false);
    }

    // Role-based gate, for routes without a backing permission yet (see route data `roles`)
    const requiredRoles: UserRoleType[] | undefined = route.data['roles'];
    if (requiredRoles && requiredRoles.length > 0 && !this.authService.hasAnyRole(requiredRoles)) {
      this.handleUnauthorized();
      return of(false);
    }

    // If no permission is required, allow access
    const requiredPermission = route.data['permission'];
    if (!requiredPermission) {
      return of(true);
    }

    // Check if user has the required permission
    return this.validatePermission(requiredPermission);
  }

  /**
   * Validates if the user has the required permission(s).
   * An array means "any one of these" (OR semantics).
   * Loads permissions from API if not already loaded
   */
  private validatePermission(permissionId: number | number[]): Observable<boolean> {
    return this.store.select(FeatureSessionSelectors.selectLoadedPermissions).pipe(
      switchMap((arePermissionsLoaded) => {
        if (arePermissionsLoaded) {
          return this.checkStoredPermission(permissionId);
        } else {
          return this.loadPermissionsAndCheck(permissionId);
        }
      }),
      tap((hasPermission) => {
        if (!hasPermission) {
          this.handleUnauthorized();
        }
      }),
      catchError((error) => {
        console.error('Error validating permission:', error);
        this.handleUnauthorized();
        return of(false);
      })
    );
  }

  /**
   * Checks the current permission (or, for an array, any one of them) against the store.
   */
  private checkStoredPermission(permissionId: number | number[]): Observable<boolean> {
    return Array.isArray(permissionId) ? this.permissionService.canAny(...permissionId) : this.permissionService.can(permissionId);
  }

  /**
   * Loads user permissions from API and checks the required permission(s)
   */
  private loadPermissionsAndCheck(permissionId: number | number[]): Observable<boolean> {
    return this.authService.getUserRolePermissions().pipe(
      tap((userRoles) => {
        const currentUser = this.authService.currentUser();
        if (!currentUser) {
          throw new Error('User not found in session');
        }

        const enabledPermissions = userRoles.rolPermissions
          .filter((rolePermission) => rolePermission.enable)
          .map((rolePermission) => rolePermission.permissionNumber);

        this.store.dispatch(
          FeatureSessionActions.setSession({
            user: currentUser,
            permissions: enabledPermissions
          })
        );
      }),
      switchMap(() => this.checkStoredPermission(permissionId))
    );
  }

  /**
   * Handles unauthenticated users by redirecting to login
   */
  private handleUnauthenticated(currentUrl: string): void {
    // A "Sign in as" tab that ended (e.g. reloaded after it expired) shows why, and never offers the login page.
    const endReason = this.authService.signInAsEndReason();
    if (endReason) {
      this.router.navigate(['/', SignInAsRoute.Ended], { queryParams: { reason: endReason } });
      return;
    }

    // Store the URL the user was trying to access
    sessionStorage.setItem('redirectAfterLogin', currentUrl);

    // Clear any stale auth data
    this.authService.logOut();

    // Redirect to login with current query params
    const queryParams = getCurrentQueryParams();
    this.router.navigate(['/login'], { queryParams });
  }

  /**
   * Handles unauthorized access by redirecting to access denied page
   */
  private handleUnauthorized(): void {
    this.router.navigate(['/accessdenied'], { queryParamsHandling: 'merge' });
  }
}
